<!--
    Review Template
-->
<template>
  <v-hover v-slot="{ hover }">
    <!-- Favorite -->
    <v-card
      v-if="getType === 'favorite'"
      @click="navigateToWork"
      v-ripple
      rounded
      class="favorite d-flex flex-column rounded-xl"
      :elevation="hover ? activeHover : initialHover"
    >
      <v-img :src="image" :class="{ hovered: hover }">
        <template v-slot:placeholder>
          <v-row class="fill-height ma-0" align="center" justify="center">
            <v-progress-circular indeterminate color="info"></v-progress-circular>
          </v-row>
        </template>

        <medium-icon :medium="medium" :hover="true" :text="title" class="overlay-icon" />
      </v-img>
    </v-card>

    <!-- Small Individual Review -->
    <v-card
      v-else-if="getType === 'sm-individual'"
      v-on:click="displayDrawer"
      v-ripple
      rounded
      class="individual d-flex flex-column rounded-xl"
      :elevation="hover ? activeHover : initialHover"
      width="250"
      max-height="375"
    >
      <v-img height="250" width="250" :src="image" :class="{ hovered: hover }">
        <template v-slot:placeholder>
          <v-row class="fill-height ma-0" align="center" justify="center">
            <v-progress-circular indeterminate color="info"></v-progress-circular>
          </v-row>
        </template>

        <medium-icon :medium="medium" class="overlay-icon" />

        <rating-circle class="overlay-icon" :size="40" :rating="rating" :opacity="0.65"></rating-circle>
      </v-img>

      <div class="review-content">
        <v-card-title class="review-title">{{ title }}</v-card-title>

        <v-card-text v-if="review !== ''" class="review-text">{{ text }}</v-card-text>
      </div>
    </v-card>

    <!-- Large Individual Review -->
    <v-card
      v-else-if="getType === 'lg-individual'"
      v-on:click="displayDrawer"
      v-ripple
      rounded
      class="individual d-flex flex-column rounded-xl"
      :elevation="hover ? activeHover : initialHover"
      min-height="300"
      max-height="500"
      width="250"
    >
      <v-img height="375" width="250" :src="image" :class="{ hovered: hover }">
        <template v-slot:placeholder>
          <v-row class="fill-height ma-0" align="center" justify="center">
            <v-progress-circular indeterminate color="info"></v-progress-circular>
          </v-row>
        </template>

        <medium-icon :medium="medium" class="overlay-icon" />

        <rating-circle class="overlay-icon" :size="40" :rating="rating" :opacity="0.65"></rating-circle>
      </v-img>

      <div class="review-content">
        <v-card-title class="review-title">{{ title }}</v-card-title>

        <v-card-text class="review-text">
          <span v-if="!review.spoiler">{{ text }}</span>
          <em v-else class="red--text">Contains spoilers!</em>
        </v-card-text>
      </div>
    </v-card>

    <!-- Small Other Review -->
    <v-card
      v-else-if="getType === 'sm-general'"
      v-on:click="displayDrawer"
      v-ripple
      rounded
      class="general d-flex flex-column rounded-xl"
      :elevation="hover ? activeHover : initialHover"
      height="325"
      width="250"
    >
      <v-img height="200" width="250" :src="image" :class="{ hovered: hover }">
        <template v-slot:placeholder>
          <v-row class="fill-height ma-0" align="center" justify="center">
            <v-progress-circular indeterminate color="info"></v-progress-circular>
          </v-row>
        </template>

        <medium-icon :medium="medium" class="overlay-icon" />

        <rating-circle class="overlay-icon" :size="40" :rating="rating" :opacity="0.65"></rating-circle>
      </v-img>

      <div class="review-content">
        <profile-avatar
          class="review-reviwer"
          :size="64"
          :username="username"
          :preset-profile-pic="review.author.username"
        />

        <v-card-title class="review-title">{{ title }}</v-card-title>

        <v-card-text class="review-text">{{ text }}</v-card-text>
      </div>
    </v-card>

    <!-- Large Other Review -->
    <v-card
      v-else-if="getType === 'lg-general'"
      v-on:click="displayDrawer"
      v-ripple
      rounded
      class="general d-flex flex-column rounded-xl"
      :elevation="hover ? activeHover : initialHover"
      min-height="300"
      max-height="425"
      width="250"
    >
      <v-img height="300" width="250" :src="image" :class="{ hovered: hover }">
        <template v-slot:placeholder>
          <v-row class="fill-height ma-0" align="center" justify="center">
            <v-progress-circular indeterminate color="info"></v-progress-circular>
          </v-row>
        </template>

        <medium-icon :medium="medium" class="overlay-icon" />

        <rating-circle class="overlay-icon" :size="40" :rating="rating" :opacity="0.65"></rating-circle>
      </v-img>

      <div class="review-content">
        <profile-avatar
          class="review-reviwer"
          :size="64"
          :username="username"
          :preset-profile-pic="review.author.username"
        />

        <v-card-title class="review-title">{{ title }}</v-card-title>

        <v-card-text class="review-text">{{ text }}</v-card-text>
      </div>
    </v-card>

    <!-- Add Tile -->
    <v-card
      v-else-if="getType === 'add-desktop'"
      v-on:click="createReview"
      v-ripple
      rounded
      class="general d-flex flex-column rounded-xl justify-center"
      :elevation="hover ? activeHover : initialHover"
      height="250"
      width="250"
      outlined
    >
      <v-icon size="50" color="#292929">mdi-plus</v-icon>
      <div class="text-center">Create a Review</div>
    </v-card>

    <v-card v-else></v-card>
  </v-hover>
</template>

<style scoped>
.v-card {
  margin: auto;

  cursor: pointer;

  overflow: hidden;

  transition: box-shadow 0.25s ease-out 0.1s;
}

/* @COMMENT Still deciding on whether we should keep this, or if the box-shadow is enough */
.v-card:not(.favorite) .v-image >>> .v-image__image {
  transition: opacity 0.25s ease-out 0.1s;
}
.v-card:not(.favorite) .v-image.hovered >>> .v-image__image {
  opacity: 0.85;
}

/* Icon detailing the medium of media i.e. film, album, etc. */
.v-card .overlay-icon {
  position: absolute !important;
  margin: 8px;
}
.overlay-icon.medium-icon {
  top: 0;
  left: 0;
}
.overlay-icon.rating-circle {
  top: 0;
  right: 0;
}

/*
        Favorite Specific
    */

.v-card.favorite:hover .medium-icon {
  max-width: calc(100% - 16px) !important;

  background-color: rgba(0, 0, 0, 0.75);
}

.v-card.favorite .medium-icon >>> .span-wrapper {
  overflow: hidden;
  max-width: calc(100% - 40px);
}

/*
        Review Content
    */
.v-card .review-content {
  position: relative;

  min-height: 0;
}

/*
        Personal Specific
    */

/*
        Other Specific
    */

.v-card.general .review-content .v-avatar {
  position: absolute;

  top: 0;
  left: 8px;

  margin: 0;

  transform: translateY(-50%);

  background-color: white;
}

.v-card .review-content .review-title {
  display: block;

  white-space: nowrap;
  text-overflow: ellipsis;

  overflow: hidden;

  padding: 4px 0;
}

.v-card.general .review-content .review-title {
  margin-left: 80px;
  width: calc(100% - 80px);
}
.v-card.individual .review-content .review-title {
  margin-left: 16px;
  width: calc(100% - 16px);
}

.v-card .review-content .review-text {
  overflow: hidden;
}

.v-card .review-content .review-text:before {
  content: "";

  position: absolute;
  left: 0;
  top: 0;

  width: 100%;
  height: 100%;

  background: linear-gradient(transparent 25%, white 100%);
}
</style>

<script>
import { mapMutations } from "vuex";
import ratingCircle from "./rating-circles/rating-circle-monochrome.vue";

let types = {
  favorite: "Favorite",
};

export default {
  name: "review-tile",

  components: {
    ratingCircle,
  },

  data: () => ({
    initialHover: 2,

    activeHover: 5,
  }),

  props: {
    /*
				Full Object
		*/
    data: { type: Object, default: () => ({}) },

    /*
				"individual"
				"general"
				"favorite"
			*/
    type: { type: String, default: types.favorite },

    width: {
      type: [Number, String],
      default: 200,
    },
  },

  computed: {
    review() {
      return this.data;
    },

    /*
				Defaults
			*/
    // film, book, podcast, album
    medium() {
      if (this.type === "favorite") {
        return this.review.medium;
      }
      return this.review.work.medium || "";
    },
    image() {
      if (this.type === "favorite") {
        return this.review.poster_source || "https://telemonial.com/unknown.png";
      }
      return this.review.work.posterSource || "https://telemonial.com/unknown.png";
    },
    username() {
      return this.review.author.username || "";
    },
    title() {
      if (this.type === "favorite") {
        return this.review.title;
      }
      return this.review.work.title || "Unknown Title";
    },
    text() {
      return this.review.review || "";
    },
    // TODO: Remove default
    rating() {
      return this.review.rating || 0;
    },
    // TODO: Remove default
    id() {
      if (this.type === "favorite") {
        return this.review.id;
      }
      const idNum = this.review.work.id || 0;
      return idNum.toString();
    },

    getRatingIcon() {
      return `mdi-numeric-${this.rating}`;
    },

    /*
                Types:
                favorite
                sm-general
                lg-general
                sm-individual
                lg-individual
            */
    getType() {
      if (this.type === "individual")
        return this.medium === "film" || this.medium === "book" || this.medium === "tvShow"
          ? "lg-individual"
          : "sm-individual";
      else if (this.type === "general")
        return this.medium === "film" || this.medium === "book" || this.medium === "tvShow"
          ? "lg-general"
          : "sm-general";
      else if (this.type === "favorite") return "favorite";
      else if (this.type === "add") return "add-desktop";
      else return "favorite";
    },
  },

  methods: {
    ...mapMutations(["showDrawer", "closeDrawer"]),

    displayDrawer() {
      this.showDrawer({
        status: "review",
        data: this.review,
      });
    },

    createReview() {
      this.$router.push("/rate");
    },

    async navigateToWork() {
      let workId = null;
      if (this.type === "favorite") {
        workId = this.id;
      } else {
        workId = this.review.work.id;
      }
      await this.$router.push({
        name: "work",
        params: {
          medium: this.medium,
          workId: workId,
        },
      });
    },
  },
};
</script>
